<script setup lang="ts">
import TableShowParameters from '../table/TableShowParameters.vue';
import ResultLens from '../results/ResultLens.vue';
import ResultLensTable from '../table/ResultLensTable.vue';
import ResultToricSteps from '../table/ResultToricSteps.vue';
import EyeAxisDiagram from '../results/EyeAxisDiagram.vue';
import CardTitleWithAbbreviation from './CardTitleWithAbbreviation.vue';
import Card from './Card.vue';
import CardFooter from './CardFooter.vue';
import { computed, type PropType } from 'vue';
import type { CalculatorResultData } from '@/models/CalculatorResult';

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  text: {
    type: String,
    required: true
  },
  calculatorResultData: {
    type: Array as PropType<CalculatorResultData[]>,
    required: false
  },
  formula: {
    type: String,
    required: false
  },
  /** Usado só pra espelhar o rótulo "Temporal" no desenho do eixo. */
  eye: {
    type: String as PropType<'od' | 'os'>,
    required: false,
    default: 'od'
  },
  // Recurso em teste — controla a exibição de tudo que é tórico no
  // resultado (desenho do eixo, "Cyl" no cabeçalho, tabela por degrau).
  // O backend continua calculando/devolvendo isso normalmente; aqui é só
  // display, atrás da mesma trava (?beta=torica) do formulário.
  showTorico: {
    type: Boolean,
    required: false,
    default: false
  },
  // Usado só pra esconder ACD/LT/WTW na Afacia (ver TableShowParameters) —
  // essa fórmula não usa esses dados, e o payload manda 0 neles.
  formulaType: {
    type: String,
    required: false
  }
})

// Só existe mais de uma lente por olho quando o modo tórica está desligado
// (fora do modo tórica não há tabela por degrau) — então basta olhar o
// primeiro resultado pra saber se esse olho tem tórica calculada.
const toricResult = computed(() => {
    if (!props.showTorico) return undefined
    return props.calculatorResultData?.find((result) => result.toricSteps && result.toricSteps.length)
})
</script>

<template>
    <Card>
        <div class="card-header">
            <CardTitleWithAbbreviation :title="title" :text="text" :formula="formula" />

            <div class="card-header__eye" v-if="toricResult">
                <!-- Eixo BASE (córnea, pré-lente) — não o resíduo do degrau
                     recomendado. É onde a lente tórica de verdade é
                     posicionada (perto do meridiano mais curvo/steep); o
                     eixo residual (getDisplayAstigmatism) fica perto de
                     zero quando a correção é boa, o que o torna instável
                     e podia apontar pertinho do eixo plano por acidente
                     numérico — foi o que o professor notou. -->
                <EyeAxisDiagram
                    :axis="toricResult.axis as number"
                    :incision="toricResult.eyeParameters.k.incision"
                    :sia="toricResult.eyeParameters.k.sia"
                    :eye="eye"
                />
            </div>
        </div>

        <div class="card__content" v-if="calculatorResultData && calculatorResultData.length">
            <div class="table-show-parameters">
                <TableShowParameters :eyeParameters="calculatorResultData[0].eyeParameters" :show-sia="showTorico" :show-acd-lt-wtw="formulaType !== 'Afacia'" />
            </div>

            <div class="result-lens__container">
                <div class="result-lens__item" v-for="(result, index) in calculatorResultData" :key="index">
                    <hr class="separator">
                    <article class="result-lens">
                        <ResultLens :titulo="'Lens ' + (index + 1)" :target="result.target" :aConstant="result.aConstant" :id-len="result.idLen" :cylinder="showTorico ? result.cylinder : undefined" :axis="showTorico ? result.axis : undefined" :toric-steps="showTorico ? result.toricSteps : undefined" />
                        <div class="combined-tables" :class="{ 'combined-tables--toric': showTorico && result.toricSteps && result.toricSteps.length }">
                            <ResultLensTable :resultData="result" />
                            <ResultToricSteps v-if="showTorico && result.toricSteps && result.toricSteps.length" :steps="result.toricSteps" />
                        </div>
                    </article>
                </div>
            </div>

            <hr class="separator">
            <article class="result-lens">
                <CardFooter/>
            </article>

        </div>

        <p class="parameters__null" v-else>{{$t('result.no_parameters')}}</p>
    </Card>
</template>

<style scoped lang="sass">
@use "../../assets/colors/colors"
@use "../../assets/fonts/fonts"

// Título do olho (OD/OE) e o desenho do eixo — só aparece o desenho quando
// esse olho tem tórica calculada. O desenho fica centralizado no espaço que
// sobra à direita do título (não colado nele, não jogado na borda) — e como
// esse espaço é quem quebra pra linha de baixo no celular (não o título),
// o desenho continua centralizado (embaixo do título) em telas estreitas
// também, em vez de ficar preso na esquerda.
.card-header
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 1em

  &__eye
    flex: 1 1 220px
    min-width: 0
    display: flex
    justify-content: center

.parameters__null
    font-size: 1.4em
    text-align: center
    padding-top: 2em
    padding-bottom: 2em
    color: colors.$secondary
    font-weight: 400

.card
  @media screen
    box-shadow: 2px 0 4px rgba(0,0,0,0.25)

    &__content
        color: colors.$secondary

  @media print
    border: 1px solid #000000

.table-show-parameters
  padding: 2.3em 7.43%

.separator
    margin: 0
    border:
        top:
            style: solid
            width: 1px
        left: none
        right: none
        bottom: none

    @media screen
        border-top-color: colors.$secondary-light

    @media print
        border-top-color: colors.$print

// Junta visualmente a tabela de IOL Power/SE com a de degraus tóricos (lado
// a lado, com uma borda só em volta das duas) — só quando existe tabela por
// degrau; sem tórica, a tabela de IOL Power/SE fica como sempre foi.
.combined-tables
  &--toric
    display: flex
    align-items: stretch
    gap: 0
    border: 1px solid colors.$secondary-light
    border-radius: 0.6em
    overflow: hidden
    margin-top: 0.8em

    @media print
      border-color: colors.$print

    :deep(.result-lens__result-container)
      flex: 1 1 0
      min-width: 0

    :deep(.toric-steps)
      flex: 1 1 0
      min-width: 0
      margin-top: 0
      padding: 0 0.8em
      border-left: 1px solid colors.$secondary-light

      @media print
        border-left-color: colors.$print

.result-lens
  padding: 0 7.43% 2.08em 7.43%

  &__item
    min-width: 50%
    max-width: 100%
    flex-grow: 1
    flex-basis: auto
    box-sizing: border-box
    &:nth-child(even)
        border-left: 1px solid colors.$primary-contrast
        // Sem max-width fixo: quando os dois cabem lado a lado, o min-width:
        // 50% de cada um já garante a divisão igual. Só importa pra quando
        // um card "cai" pra linha de baixo (conteúdo largo demais pra
        // caber nos 50%) — aí ele preenche a linha toda em vez de ficar
        // preso na metade com espaço sobrando.

  &__container
    display: flex
    flex-wrap: wrap

</style>